import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, FileText, Search } from 'lucide-react';
import { AppError } from '@/core/errors/AppError';
import { useSession } from '@/core/auth/useSession';
import { useLiquidaciones } from '@/data/hooks/useLiquidaciones';
import { useEmpleados, usePromotores } from '@/data/hooks/useCatalogos';
import { ESTADOS_LIQUIDACION, TIPOS_LIQUIDACION, } from '@/data/dto/liquidacion';
import { formatearFecha, formatearMonto } from '@/core/utils/formatters';
import { Button } from '@/shared/components/Button';
import { Chip } from '@/shared/components/Chip';
import { Input } from '@/shared/components/Input';
import { Select } from '@/shared/components/Select';
import { Table } from '@/shared/components/Table';
import { GenerarLiquidacionDialog } from './GenerarLiquidacionDialog';
import { LiquidacionDetalleDialog } from './LiquidacionDetalleDialog';
const POR_PAGINA = 100;
const MESES = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];
function isoFecha(y, m, d) {
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function diasDelMes(anio, mes) {
    return new Date(anio, mes + 1, 0).getDate();
}
function quincenaActual() {
    const d = new Date();
    return {
        anio: d.getFullYear(),
        mes: d.getMonth(),
        mitad: d.getDate() <= 15 ? 1 : 2,
    };
}
function quincenaAnterior(q) {
    if (q.mitad === 2)
        return { ...q, mitad: 1 };
    const mes = q.mes === 0 ? 11 : q.mes - 1;
    const anio = q.mes === 0 ? q.anio - 1 : q.anio;
    return { anio, mes, mitad: 2 };
}
function quincenaSiguiente(q) {
    if (q.mitad === 1)
        return { ...q, mitad: 2 };
    const mes = q.mes === 11 ? 0 : q.mes + 1;
    const anio = q.mes === 11 ? q.anio + 1 : q.anio;
    return { anio, mes, mitad: 1 };
}
function mismaQuincena(a, b) {
    return a.anio === b.anio && a.mes === b.mes && a.mitad === b.mitad;
}
function rangoQuincena(q) {
    if (q.mitad === 1) {
        return { desde: isoFecha(q.anio, q.mes, 1), hasta: isoFecha(q.anio, q.mes, 15) };
    }
    return {
        desde: isoFecha(q.anio, q.mes, 16),
        hasta: isoFecha(q.anio, q.mes, diasDelMes(q.anio, q.mes)),
    };
}
function etiquetaQuincena(q) {
    const finDia = q.mitad === 1 ? 15 : diasDelMes(q.anio, q.mes);
    const desdeDia = q.mitad === 1 ? 1 : 16;
    return `${desdeDia}–${finDia} ${MESES[q.mes]} ${q.anio}`;
}
/** Genera las últimas N quincenas terminando en la actual (la actual primero). */
function ultimasQuincenas(n) {
    const out = [];
    let q = quincenaActual();
    for (let i = 0; i < n; i++) {
        out.push(q);
        q = quincenaAnterior(q);
    }
    return out;
}
function keyQuincena(q) {
    return `${q.anio}-${q.mes}-${q.mitad}`;
}
const TONO_ESTADO = {
    BORRADOR: 'warning',
    APROBADA: 'info',
    PAGADA: 'success',
    ANULADA: 'neutral',
};
export function LiquidacionesPage() {
    const { tieneRol, esPromotor } = useSession();
    const puedeGenerar = tieneRol('ROLE_GENERAR_LIQUIDACION');
    const vistaPromotor = esPromotor;
    const [tipo, setTipo] = useState('');
    const [estado, setEstado] = useState('');
    const [busqueda, setBusqueda] = useState('');
    const [quincena, setQuincena] = useState(() => quincenaActual());
    const [generarOpen, setGenerarOpen] = useState(false);
    const [verId, setVerId] = useState(null);
    const { data: empleados } = useEmpleados({ enabled: !vistaPromotor });
    const { data: promotores } = usePromotores({ enabled: !vistaPromotor });
    const rango = rangoQuincena(quincena);
    const esQuincenaActual = mismaQuincena(quincena, quincenaActual());
    const { data, isLoading, isError, error, refetch } = useLiquidaciones({
        tipo: tipo || undefined,
        estado: estado || undefined,
        pagina: 1,
        porPagina: POR_PAGINA,
        rangoHastaDesde: rango.desde,
        rangoHastaHasta: rango.hasta,
    });
    const ultimas12 = useMemo(() => ultimasQuincenas(12), []);
    const nombrePersona = (l) => {
        if (l.personaNombre)
            return l.personaNombre;
        if (l.tipo === 'INSTRUCTOR' && l.empleadoId) {
            const e = empleados?.find((x) => x.id === l.empleadoId);
            return e ? `${e.nombre} ${e.apellido}` : l.empleadoId.slice(0, 8);
        }
        if (l.tipo === 'PROMOTOR' && l.promotorId) {
            const p = promotores?.find((x) => x.id === l.promotorId);
            return p ? p.nombre : l.promotorId.slice(0, 8);
        }
        return '—';
    };
    // Construye las filas agrupadas por persona. Una persona puede tener
    // liquidacion como INSTRUCTOR, como PROMOTOR, o ambas en el mismo
    // periodo: en ese caso la mostramos en una sola fila con ambos chips y
    // total sumado.
    const filas = useMemo(() => {
        if (vistaPromotor) {
            // En vista promotor cada liquidacion es una fila propia (el back ya
            // restringe a su personaId).
            return (data?.items ?? []).map((liq) => ({
                key: liq.id,
                nombre: liq.personaNombre ?? '—',
                roles: [liq.tipo],
                liquidaciones: [liq],
                totalSumado: Number(liq.total) || 0,
                fechaPagoMasReciente: liq.fechaPago ?? null,
            }));
        }
        const empleadoById = new Map(empleados?.map((e) => [e.id, e]) ?? []);
        const promotorById = new Map(promotores?.map((p) => [p.id, p]) ?? []);
        // Resolver una clave estable por persona. Empleados con uusuarioId
        // se agrupan por usuario; promotores internos (con empleadoId) heredan
        // la clave del empleado; promotores externos van por su propio id.
        const keyParaEmpleado = (empId) => {
            const emp = empleadoById.get(empId);
            if (emp?.uusuarioId)
                return `usr:${emp.uusuarioId}`;
            return `emp:${empId}`;
        };
        const keyParaPromotor = (proId) => {
            const pro = promotorById.get(proId);
            if (pro?.empleadoId)
                return keyParaEmpleado(pro.empleadoId);
            return `pro:${proId}`;
        };
        const mapa = new Map();
        const obtener = (key, init) => {
            let fila = mapa.get(key);
            if (!fila) {
                fila = { key, ...init() };
                mapa.set(key, fila);
            }
            return fila;
        };
        // 1) Liquidaciones reales del periodo.
        for (const liq of data?.items ?? []) {
            const key = liq.tipo === 'INSTRUCTOR' && liq.empleadoId
                ? keyParaEmpleado(liq.empleadoId)
                : liq.tipo === 'PROMOTOR' && liq.promotorId
                    ? keyParaPromotor(liq.promotorId)
                    : `liq:${liq.id}`;
            const fila = obtener(key, () => ({
                nombre: nombrePersona(liq),
                roles: [],
                liquidaciones: [],
                totalSumado: 0,
                fechaPagoMasReciente: null,
                empleadoId: liq.empleadoId ?? undefined,
                promotorId: liq.promotorId ?? undefined,
            }));
            if (!fila.roles.includes(liq.tipo))
                fila.roles.push(liq.tipo);
            fila.liquidaciones.push(liq);
            if (liq.estado !== 'ANULADA')
                fila.totalSumado += Number(liq.total) || 0;
            if (liq.fechaPago) {
                if (!fila.fechaPagoMasReciente || liq.fechaPago > fila.fechaPagoMasReciente) {
                    fila.fechaPagoMasReciente = liq.fechaPago;
                }
            }
            if (liq.empleadoId)
                fila.empleadoId = liq.empleadoId;
            if (liq.promotorId)
                fila.promotorId = liq.promotorId;
        }
        // 2) Placeholders para todas las personas activas sin liquidacion.
        for (const e of empleados ?? []) {
            if (e.activo === false)
                continue;
            const key = keyParaEmpleado(e.id);
            const fila = obtener(key, () => ({
                nombre: `${e.nombre} ${e.apellido}`.trim(),
                roles: [],
                liquidaciones: [],
                totalSumado: 0,
                fechaPagoMasReciente: null,
                empleadoId: e.id,
                email: e.email ?? undefined,
                telefono: e.telefono ?? undefined,
            }));
            if (!fila.roles.includes('INSTRUCTOR'))
                fila.roles.push('INSTRUCTOR');
            if (!fila.empleadoId)
                fila.empleadoId = e.id;
            if (!fila.email)
                fila.email = e.email ?? undefined;
            if (!fila.telefono)
                fila.telefono = e.telefono ?? undefined;
        }
        for (const p of promotores ?? []) {
            if (p.activo === false)
                continue;
            const key = keyParaPromotor(p.id);
            const fila = obtener(key, () => ({
                nombre: p.nombre,
                roles: [],
                liquidaciones: [],
                totalSumado: 0,
                fechaPagoMasReciente: null,
                promotorId: p.id,
                email: p.email ?? undefined,
                telefono: p.telefono ?? undefined,
            }));
            if (!fila.roles.includes('PROMOTOR'))
                fila.roles.push('PROMOTOR');
            if (!fila.promotorId)
                fila.promotorId = p.id;
            if (!fila.email)
                fila.email = p.email ?? undefined;
            if (!fila.telefono)
                fila.telefono = p.telefono ?? undefined;
        }
        // Ordenar: con liquidacion primero (por nombre), despues sin liq (por nombre).
        return Array.from(mapa.values()).sort((a, b) => {
            const aTiene = a.liquidaciones.length > 0 ? 1 : 0;
            const bTiene = b.liquidaciones.length > 0 ? 1 : 0;
            if (aTiene !== bTiene)
                return bTiene - aTiene;
            return a.nombre.localeCompare(b.nombre);
        });
    }, [data, empleados, promotores, vistaPromotor]);
    // Aplica filtros de tipo, estado y busqueda libre sobre filas agrupadas.
    const filasFiltradas = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        return filas.filter((f) => {
            if (tipo && !f.roles.includes(tipo))
                return false;
            if (estado) {
                // El filtro de estado solo aplica si la persona tiene al menos una
                // liquidacion en ese estado.
                if (!f.liquidaciones.some((l) => l.estado === estado))
                    return false;
            }
            if (q) {
                const haystack = `${f.nombre} ${f.email ?? ''} ${f.telefono ?? ''}`.toLowerCase();
                if (!haystack.includes(q))
                    return false;
            }
            return true;
        });
    }, [filas, tipo, estado, busqueda]);
    const columnas = useMemo(() => {
        const cols = [];
        if (!vistaPromotor) {
            cols.push({
                key: 'persona',
                header: 'Persona',
                cell: (f) => _jsx("span", { className: "text-sm text-text-primary", children: f.nombre }),
            });
            cols.push({
                key: 'tipo',
                header: 'Tipo',
                width: '180px',
                cell: (f) => (_jsxs("div", { className: "flex items-center gap-1", children: [f.roles.includes('INSTRUCTOR') ? (_jsx(Chip, { tono: "info", children: "Instructor" })) : null, f.roles.includes('PROMOTOR') ? (_jsx(Chip, { tono: "primary", children: "Promotor" })) : null] })),
            });
        }
        cols.push({
            key: 'periodo',
            header: 'Período',
            cell: () => (_jsxs("span", { className: "text-xs text-text-tertiary", children: [formatearFecha(rango.desde), " \u2192 ", formatearFecha(rango.hasta)] })),
            width: '220px',
        }, {
            key: 'total',
            header: 'Total',
            align: 'right',
            width: '160px',
            cell: (f) => {
                if (f.liquidaciones.length === 0) {
                    return _jsx("span", { className: "num text-text-tertiary", children: "\u2014" });
                }
                // Color basado en el "peor" estado: si hay borradores prevalece
                // warning; si hay aprobadas info; si todas pagadas verde.
                const tieneBorrador = f.liquidaciones.some((l) => l.estado === 'BORRADOR');
                const tieneAprobada = f.liquidaciones.some((l) => l.estado === 'APROBADA');
                const todasPagadas = f.liquidaciones.every((l) => l.estado === 'PAGADA' || l.estado === 'ANULADA');
                const color = tieneBorrador
                    ? 'text-warning'
                    : tieneAprobada
                        ? 'text-info'
                        : todasPagadas
                            ? 'text-success'
                            : 'text-text-primary';
                return (_jsxs("div", { className: "flex flex-col items-end", children: [_jsx("span", { className: `num font-semibold ${color}`, children: formatearMonto(f.totalSumado) }), f.liquidaciones.length > 1 ? (_jsxs("span", { className: "text-[10px] text-text-tertiary", children: [f.liquidaciones.length, " liquidaciones"] })) : null] }));
            },
        }, {
            key: 'estado',
            header: 'Estado',
            width: '200px',
            cell: (f) => {
                if (f.liquidaciones.length === 0) {
                    return (_jsx("span", { className: "text-[11px] text-text-tertiary", children: "Sin liquidaci\u00F3n en este per\u00EDodo" }));
                }
                if (f.liquidaciones.length === 1) {
                    const l = f.liquidaciones[0];
                    return _jsx(Chip, { tono: TONO_ESTADO[l.estado] ?? 'neutral', children: l.estado });
                }
                // Multiple liquidaciones: mostramos un chip por cada estado distinto.
                const estados = Array.from(new Set(f.liquidaciones.map((l) => l.estado)));
                return (_jsx("div", { className: "flex flex-wrap items-center gap-1", children: estados.map((est) => (_jsx(Chip, { tono: TONO_ESTADO[est] ?? 'neutral', children: est }, est))) }));
            },
        }, {
            key: 'fechaPago',
            header: vistaPromotor ? 'Cobrada' : 'Pagada',
            cell: (f) => {
                if (f.liquidaciones.length === 0) {
                    return _jsx("span", { className: "text-text-tertiary", children: "\u2014" });
                }
                return f.fechaPagoMasReciente ? (_jsx("span", { className: "text-success", children: formatearFecha(f.fechaPagoMasReciente) })) : (_jsx("span", { className: "text-text-tertiary", children: "\u2014" }));
            },
            width: '120px',
        });
        return cols;
    }, [vistaPromotor, rango]);
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("div", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-xl font-bold text-text-primary", children: vistaPromotor ? 'Mis liquidaciones' : 'Liquidaciones' }), _jsx("p", { className: "text-xs text-text-secondary", children: vistaPromotor
                                    ? 'Liquidaciones de tus comisiones. Mostrá el estado de cobro (pagada/pendiente).'
                                    : 'Se generan automáticamente al cierre de cada período según la frecuencia de cada persona.' })] }), puedeGenerar && !vistaPromotor ? (_jsxs(Button, { variant: "secondary", size: "sm", onClick: () => setGenerarOpen(true), children: [_jsx(FileText, { className: "h-4 w-4" }), "Liquidaci\u00F3n excepcional"] })) : null] }), _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsxs("div", { className: "flex items-center gap-1 rounded-md border border-border-subtle bg-surface px-1 py-0.5", children: [_jsx("button", { type: "button", onClick: () => setQuincena(quincenaAnterior(quincena)), className: "rounded p-1 text-text-tertiary hover:bg-surface-2 hover:text-text-primary", "aria-label": "Quincena anterior", children: _jsx(ChevronLeft, { className: "h-4 w-4" }) }), _jsxs("select", { value: keyQuincena(quincena), onChange: (e) => {
                                    const q = ultimas12.find((x) => keyQuincena(x) === e.target.value);
                                    if (q)
                                        setQuincena(q);
                                }, className: "min-w-[200px] bg-transparent px-1 text-center text-sm font-medium text-text-primary focus:outline-none", children: [!ultimas12.some((x) => mismaQuincena(x, quincena)) ? (_jsx("option", { value: keyQuincena(quincena), children: etiquetaQuincena(quincena) })) : null, ultimas12.map((q) => (_jsxs("option", { value: keyQuincena(q), children: [etiquetaQuincena(q), mismaQuincena(q, quincenaActual()) ? ' · actual' : ''] }, keyQuincena(q))))] }), _jsx("button", { type: "button", onClick: () => setQuincena(quincenaSiguiente(quincena)), disabled: esQuincenaActual, className: "rounded p-1 text-text-tertiary hover:bg-surface-2 hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-40", "aria-label": "Quincena siguiente", children: _jsx(ChevronRight, { className: "h-4 w-4" }) })] }), !vistaPromotor ? (_jsxs("div", { className: "relative w-64", children: [_jsx(Search, { className: "pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" }), _jsx(Input, { value: busqueda, onChange: (e) => setBusqueda(e.target.value), placeholder: "Buscar nombre, email o tel\u00E9fono", className: "h-9 pl-8 text-xs" })] })) : null, !vistaPromotor ? (_jsxs(Select, { value: tipo, onChange: (e) => setTipo(e.target.value), className: "w-44", children: [_jsx("option", { value: "", children: "Instructores y promotores" }), TIPOS_LIQUIDACION.map((t) => (_jsx("option", { value: t, children: t === 'INSTRUCTOR' ? 'Solo instructores' : 'Solo promotores' }, t)))] })) : null, _jsxs(Select, { value: estado, onChange: (e) => setEstado(e.target.value), className: "w-40", children: [_jsx("option", { value: "", children: "Todos los estados" }), ESTADOS_LIQUIDACION.map((e) => (_jsx("option", { value: e, children: e }, e)))] })] }), isError ? (_jsxs("div", { className: "rounded-lg border border-danger bg-danger-fill px-3 py-2 text-sm text-danger", children: [error instanceof AppError ? error.mensaje : 'Error al cargar.', ' ', _jsx("button", { onClick: () => refetch(), className: "ml-2 underline", children: "Reintentar" })] })) : (_jsxs(_Fragment, { children: [_jsx(Table, { columns: columnas, items: filasFiltradas, rowKey: (f) => f.key, onRowClick: (f) => {
                            // Abre el detalle de la primera liquidacion (si hay varias, el
                            // detalle muestra todas las de la persona en el periodo).
                            const primera = f.liquidaciones[0];
                            if (primera)
                                setVerId(primera.id);
                        }, cargando: isLoading, vacioTitulo: "Sin coincidencias", vacioDescripcion: "Prob\u00E1 con otros filtros o limpi\u00E1 la b\u00FAsqueda.", vacioAccion: puedeGenerar && !vistaPromotor ? (_jsxs(Button, { variant: "secondary", onClick: () => setGenerarOpen(true), children: [_jsx(FileText, { className: "h-4 w-4" }), "Liquidaci\u00F3n excepcional"] })) : null }), filasFiltradas.length > 0 ? (_jsxs("div", { className: "text-right text-xs text-text-tertiary", children: [filasFiltradas.filter((f) => f.liquidaciones.length > 0).length, " con liquidaci\u00F3n", ' · ', filasFiltradas.filter((f) => f.liquidaciones.length === 0).length, " sin movimientos"] })) : null] })), _jsx(GenerarLiquidacionDialog, { open: generarOpen, onOpenChange: setGenerarOpen, onGenerada: (id) => setVerId(id) }), _jsx(LiquidacionDetalleDialog, { open: Boolean(verId), onOpenChange: (open) => !open && setVerId(null), liquidacionId: verId })] }));
}
